import React, { useState, useEffect, useCallback } from "react";
import * as S from "./style";
import { Building2, Star, DollarSign, Download } from "lucide-react";
import Table from "@components/table";
import { Reports, Zone } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import useConfig from "@hooks/useConfig";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";

import { useNavigate } from "react-router-dom";
import { APP_BASE_URL, API_KEY } from "@utils/request";
import { toast } from "react-toastify";


const StoreWiseReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { settings: currencySettings } = useGeneralSettings();
  const { bucketUrl } = useConfig();

  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zones } = useSelector((state: RootState) => state.zones);

  useEffect(() => {
    Zone.list({ state: "zones" });
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    moduleId: "",
    zoneId: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await Reports.getStoreReport({
        params: {
          page,
          limit: 10,
          ...filters,
        },
      });
      if (res) {
        setData(res.data || []);
        setTotalItems(res.meta?.total || 0);
        setTotalPages(res.meta?.totalPages || 0);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleFilterChange = (key: string, value: string) => {
    let updates: any = { [key]: value };
    if (key === "dateRange") {
      const today = new Date();
      if (value === "today") {
        updates.startDate = today.toISOString().split("T")[0];
        updates.endDate = updates.startDate;
      } else if (value === "this_week") {
        const firstDay = new Date(
          today.setDate(today.getDate() - today.getDay()),
        );
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_month") {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_year") {
        const firstDay = new Date(today.getFullYear(), 0, 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "custom") {
        // preserve current or let user pick
      } else {
        updates.startDate = "";
        updates.endDate = "";
      }
    }
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    const toastId = toast.info("Generating Excel report, please wait...", { autoClose: false });
    try {
      const user = Helper.getUser();
      const token = user?.token;
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.moduleId) queryParams.append("moduleId", filters.moduleId);
      if (filters.zoneId) queryParams.append("zoneId", filters.zoneId);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);
      
      const downloadUrl = `${APP_BASE_URL}/admin/reports/stores/export?${queryParams.toString()}`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Api-Key": API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export Excel report");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `store_performance_report_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.update(toastId, {
        render: "Excel report exported successfully!",
        type: "success",
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.update(toastId, {
        render: error?.message || "Failed to export Excel report",
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };


  const totalEarnings = data.reduce(
    (acc, curr: any) => acc + (curr.earnings || 0),
    0,
  );

  const stats = [
    {
      label: "Active Stores",
      value: `${totalItems} Stores`,
      Icon: Building2,
      color: "#1890ff",
      bgColor: "#e6f7ff",
    },
    {
      label: "Page Revenue",
      value: Helper.formatCurrency(totalEarnings, currencySettings),
      Icon: DollarSign,
      color: "#52c41a",
      bgColor: "#f6ffed",
    },
    {
      label: "Top Module",
      value: data[0]?.module || "N/A",
      Icon: Star,
      color: "#faad14",
      bgColor: "#fffbe6",
    },
  ];

  const columns = [
    {
      title: "Store Info",
      key: "name",
      render: (item: any) => {
        const imageUrl = item.avatar ? `${bucketUrl}/${item.avatar}` : null;
        return (
          <S.ProfileCard
            onClick={() =>
              navigate(`/admin/users/store/store-lists/view/${item.id}`)
            }
            style={{ cursor: "pointer" }}
          >
            {imageUrl ? (
              <S.ProfileAvatar
                src={imageUrl}
                alt={item.name}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget
                    .nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <S.ProfileAvatarPlaceholder
              variant="store"
              style={{ display: imageUrl ? "none" : "flex" }}
            >
              {item.name ? item.name.charAt(0).toUpperCase() : "?"}
            </S.ProfileAvatarPlaceholder>
            <S.ProfileDetails>
              <S.ProfileName>{item.name}</S.ProfileName>
              {item.email && <S.ProfileSubtext>{item.email}</S.ProfileSubtext>}
              {item.phone && <S.ProfileSubtext>{item.phone}</S.ProfileSubtext>}
            </S.ProfileDetails>
          </S.ProfileCard>
        );
      },
    },
    { title: "Module", key: "module" },
    { title: "Zone", key: "zone" },
    { title: "Total Orders", key: "totalOrders" },
    {
      title: "Total Earnings",
      key: "earnings",
      render: (item: any) =>
        Helper.formatCurrency(item.earnings, currencySettings),
    },
    {
      title: "Status",
      key: "status",
      render: (item: any) => {
        const isActive = item.status === "Active" || item.status === "active";
        return (
          <S.StatusPill
            bg={isActive ? "#f6ffed" : "#fff1f0"}
            color={isActive ? "#52c41a" : "#ff4d4f"}
          >
            {item.status}
          </S.StatusPill>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (item: any) => (
        <button
          onClick={() =>
            navigate(`/admin/users/store/store-lists/view/${item.id}`)
          }
          style={{
            padding: "6px 12px",
            borderRadius: "6px",
            border: "1px solid #e2e8f0",
            background: "#fff",
            fontSize: "12px",
            cursor: "pointer",
            color: "#6366f1",
            fontWeight: 500,
          }}
        >
          View
        </button>
      ),
    },
  ];

  return (
    <S.Container>
      <S.Header>
        <S.Title>Store Wise Report</S.Title>
        <S.SubTitle>
          Monitor performance trends for each store across different modules.
        </S.SubTitle>
      </S.Header>

      <S.StatsGrid>
        {stats.map((stat, i) => (
          <S.StatCard key={i} color={stat.color}>
            <S.StatIcon color={stat.color} bgColor={stat.bgColor}>
              <stat.Icon size={24} />
            </S.StatIcon>
            <S.StatInfo>
              <S.StatValue>{stat.value}</S.StatValue>
              <S.StatLabel>{stat.label}</S.StatLabel>
            </S.StatInfo>
          </S.StatCard>
        ))}
      </S.StatsGrid>

      <S.FilterSection>
        <S.FilterItem>
          <S.Label>Search Store</S.Label>
          <S.Input
            type="text"
            placeholder="Enter store name..."
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Module</S.Label>
          <S.Select
            value={filters.moduleId}
            onChange={(e) => handleFilterChange("moduleId", e.target.value)}
          >
            <option value="">All Modules</option>
            {modules?.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Zone</S.Label>
          <S.Select
            value={filters.zoneId}
            onChange={(e) => handleFilterChange("zoneId", e.target.value)}
          >
            <option value="">All Zones</option>
            {zones?.map((z: any) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Time Frame</S.Label>
          <S.Select
            value={filters.dateRange}
            onChange={(e) => handleFilterChange("dateRange", e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
            <option value="custom">Custom Time</option>
          </S.Select>
        </S.FilterItem>
        {filters.dateRange === "custom" && (
          <>
            <S.FilterItem>
              <S.Label>Start Date</S.Label>
              <S.Input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value)
                }
              />
            </S.FilterItem>
            <S.FilterItem>
              <S.Label>End Date</S.Label>
              <S.Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </S.FilterItem>
          </>
        )}
      </S.FilterSection>

      <S.TableWrapper>
        <S.TableHeader>
          <S.TableTitle>Store Performance Data ({totalItems})</S.TableTitle>
          <S.ExportButton onClick={exportExcel} disabled={exporting}>
            <Download size={16} /> {exporting ? "Exporting..." : "Download Report"}
          </S.ExportButton>
        </S.TableHeader>
        <Table
          columns={columns}
          data={data}
          loading={loading}
          totalPages={totalPages}
          onPageChange={setPage}
          pageControlled={page}
        />
      </S.TableWrapper>
    </S.Container>
  );
};

export default StoreWiseReport;
